
import React from "react";
import TocTocHeader from "@/components/TocTocHeader";
import TocTocFooter from "@/components/TocTocFooter";
import { Camera, Home, Building, TreePine, Sofa, Store, Check, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

const FotografiaImobiliaria = () => {
  const photographyTypes = [
    {
      icon: Home,
      title: "Fotografia de Casas",
      description: "Capture a essência e beleza de residências unifamiliares",
      features: ["Fachadas impactantes", "Ambientes internos", "Jardins e áreas externas", "Detalhes arquitetônicos"],
      price: "A partir de R$ 299",
      route: "/fotografia-casas"
    },
    {
      icon: Building,
      title: "Fotografia de Apartamentos",
      description: "Destaque o potencial e charme de apartamentos",
      features: ["Layouts otimizados", "Iluminação natural", "Vistas e varandas", "Decoração valorizada"],
      price: "A partir de R$ 249",
      route: "/fotografia-apartamentos"
    },
    {
      icon: TreePine,
      title: "Condomínios",
      description: "Mostre áreas comuns e diferenciais do empreendimento",
      features: ["Áreas de lazer", "Portaria e hall", "Jardins e paisagismo", "Infraestrutura completa"],
      price: "A partir de R$ 399",
      route: "/fotografia-condominios"
    },
    {
      icon: Sofa,
      title: "Interiores",
      description: "Valorize ambientes internos com técnicas especializadas",
      features: ["Decoração destacada", "Ambientes aconchegantes", "Móveis e objetos", "Composição harmônica"],
      price: "A partir de R$ 199",
      route: "/fotografia-interiores"
    },
    {
      icon: Store,
      title: "Comercial",
      description: "Destaque imóveis comerciais e corporativos",
      features: ["Fachadas comerciais", "Ambientes corporativos", "Layouts funcionais", "Localização estratégica"],
      price: "Sob consulta",
      route: "/fotografia-comercial"
    }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <TocTocHeader />
      <main className="flex-1">
        {/* Hero Section */}
        <section className="py-16 md:py-24 bg-gradient-to-r from-[#261E13] to-[#323031] text-white">
          <div className="container max-w-7xl mx-auto px-4">
            <div className="text-center max-w-4xl mx-auto">
              <div className="w-20 h-20 bg-[#FF5605]/20 rounded-3xl flex items-center justify-center mx-auto mb-8">
                <Camera className="h-10 w-10 text-[#FF5605]" />
              </div>
              <h1 className="text-4xl md:text-6xl font-bold mb-6 animate-fade-in">
                Fotografia <span className="text-[#FF5605]">Imobiliária</span>
              </h1>
              <p className="text-xl text-white/90 leading-relaxed animate-fade-in animation-delay-200">
                Valorize ainda mais o seu imóvel com fotografias profissionais que destacam cada detalhe
              </p>
            </div>
          </div>
        </section>

        {/* Photography Types Grid */}
        <section className="py-24 bg-white">
          <div className="container max-w-7xl mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-[#323031] mb-6">
                Tipos de <span className="text-[#FF5605]">Fotografia</span>
              </h2>
              <p className="text-xl text-[#5B5A59] max-w-3xl mx-auto">
                Escolha o serviço ideal para o seu tipo de imóvel
              </p>
            </div>

            <div className="grid lg:grid-cols-2 xl:grid-cols-3 gap-8">
              {photographyTypes.map((type, index) => (
                <div key={index} className="bg-white rounded-3xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300 hover:scale-105 border border-gray-100">
                  <div className="w-16 h-16 bg-[#FF5605]/10 rounded-2xl flex items-center justify-center mb-6">
                    <type.icon className="h-8 w-8 text-[#FF5605]" />
                  </div>
                  
                  <h3 className="text-2xl font-bold text-[#323031] mb-4">
                    {type.title}
                  </h3>
                  
                  <p className="text-[#5B5A59] mb-6">
                    {type.description}
                  </p>
                  
                  <ul className="space-y-3 mb-8">
                    {type.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center">
                        <Check className="h-5 w-5 text-[#FF5605] mr-3 flex-shrink-0" />
                        <span className="text-[#323031]">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <div className="text-2xl font-bold text-[#FF5605] mb-6">
                    {type.price}
                  </div>
                  
                  <Link to={type.route}>
                    <Button className="w-full bg-[#FF5605] hover:bg-[#E14D04] text-white rounded-xl py-3 group">
                      Saiba Mais
                      <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-24 bg-[#F6E5C9]/20">
          <div className="container max-w-7xl mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-[#323031] mb-6">
                Por que escolher nossa <span className="text-[#FF5605]">fotografia</span>?
              </h2>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center p-8">
                <div className="w-16 h-16 bg-[#FF5605]/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Camera className="h-8 w-8 text-[#FF5605]" />
                </div>
                <h3 className="text-2xl font-bold text-[#323031] mb-4">Equipamentos Profissionais</h3>
                <p className="text-[#5B5A59]">Utilizamos câmeras e lentes de última geração para garantir qualidade excepcional</p>
              </div>
              
              <div className="text-center p-8">
                <div className="w-16 h-16 bg-[#FF5605]/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Check className="h-8 w-8 text-[#FF5605]" />
                </div>
                <h3 className="text-2xl font-bold text-[#323031] mb-4">Edição Profissional</h3>
                <p className="text-[#5B5A59]">Tratamento e correção de cores para realçar a beleza natural do imóvel</p>
              </div>
              
              <div className="text-center p-8">
                <div className="w-16 h-16 bg-[#FF5605]/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <ArrowRight className="h-8 w-8 text-[#FF5605]" />
                </div>
                <h3 className="text-2xl font-bold text-[#323031] mb-4">Entrega Rápida</h3>
                <p className="text-[#5B5A59]">Receba suas fotos tratadas em até 48 horas após o ensaio</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gradient-to-r from-[#261E13] to-[#323031] text-white">
          <div className="container max-w-7xl mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Pronto para valorizar seu <span className="text-[#FF5605]">imóvel</span>?
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Entre em contato conosco e solicite um orçamento personalizado para seu projeto
            </p>
            <Button className="bg-[#FF5605] hover:bg-[#E14D04] text-white px-12 py-4 rounded-xl text-lg hover:scale-105 transition-transform">
              Solicitar Orçamento
            </Button>
          </div>
        </section>
      </main>
      <TocTocFooter />
    </div>
  );
};

export default FotografiaImobiliaria;
